// @ts-nocheck
/* eslint-disable */
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { Message } from '@arco-design/web-react';
import type { IndexedObject } from '@arco-design/web-react/es/Form/interface';
import qs from 'query-string';
import { baseUrl } from './utils';

export interface CommonResponse<T> {
    orderState: string;
    action: string; //动作
    code: number; //错误码
    data: T;
    message: string; //提示信息
}

export interface BaseInit {
    baseUrl?: string; // 基础路径，会拼接在 URL 前
    params?: IndexedObject;
    [key: string]: any; // 允许其他 axios 配置属性
}

export interface ReqConfig extends Omit<BaseInit, 'body'> {
    body?: IndexedObject;
}

type GetConfig = any;

const SUCCESS_CODE = 200;
const NOT_LOGIN = 101;
const NO_LOGIN = 401;
const BASE_URL = baseUrl;

// 创建 axios 实例
const axiosInstance = axios.create({
    baseURL: BASE_URL,
    withCredentials: false,
    headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
    }
});

// 请求拦截器
axiosInstance.interceptors.request.use(
    config => {
        // 可以在这里添加通用的请求处理逻辑
        // 从存储中获取 token 并添加到请求头
        // 登录接口不带旧 token：token 失效或账号不存在时，后端过滤器会直接 500，导致无法重新登录
        const isLoginRequest = config.url?.includes('/admin/login');
        const token = localStorage.getItem('authToken');
        if (token && !isLoginRequest) {
            // config.headers['Authorization'] = token;
            // 或者根据后端要求的格式
            config.headers['Authorization'] = token;
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

// 响应拦截器
axiosInstance.interceptors.response.use(
    response => {
        // 强制跳转登录页
        // 如果是 /menu/treeList 接口，直接放行，不做强制拦截
        const isMenuTreeListRequest = response.config?.url?.includes('/menu/treeList');
        if (!isMenuTreeListRequest && (response.status === NO_LOGIN || response?.data?.code === NO_LOGIN)) {
            localStorage.removeItem("authToken")
            window.location.href = "/login";
        }

        // 提取并存储服务端返回的 token（假设在响应头中）
        // 排除登录接口，因为登录接口的 token 在登录页面已经处理
        const isLoginRequest = response.config?.url?.includes('/admin/login');
        if (!isLoginRequest) {
            const token = response.headers['authorization'] || response.headers['Authorization'];
            if (token) {
                localStorage.setItem('authToken', `Bearer ${token}`);
            }
        }
        return response;
    },
    error => {
        if (error.response?.status === NO_LOGIN) {
            localStorage.removeItem("authToken")
            window.location.href = "/login";
        }
        return Promise.reject(error);
    }
);

/**
 * 获取请求Query通参
 */
// function getCommonQuery() {
//   return {
//     // os: 'web',
//     // language: 'zh-CN'
//   };
// }

const processResp = async <R>(resp: CommonResponse<R>, onError?: (msg: string) => void) => {
    // status === 其他
    if (resp.code === NOT_LOGIN) {
        if (onError) {
            onError(resp.message);
        }
        return Promise.reject(resp);
    }

    // 报错携带错误码
    if (resp.code !== SUCCESS_CODE) {
        if (onError) {
            onError(`${resp.message}`);
        } else {
            Message.error(`${resp.message}`);
        }
        return Promise.reject(resp);
    }
    const resData: any = resp || true;
    // const resData: any = resp.data || true;

    return resData;
};

export async function request<R>(url: string, init?: BaseInit, onError?: (msg: string) => void): Promise<R> {
    console.log(init?.params);
    const reqUrl = qs.stringifyUrl(
        // { url, query: { ...getCommonQuery(), ...(init?.params ?? {}) } },
        { url, query: { ...(init?.params ?? {}) } },
        { arrayFormat: 'comma' }
    );

    const config: AxiosRequestConfig = {
        url: reqUrl,
        ...init,
        params: undefined // 已经通过 qs 处理到 URL 中
    };

    try {
        const response: AxiosResponse<CommonResponse<R>> = await axiosInstance(config);
        
        return processResp(response.data, onError);
    } catch (err: any) {
        const message = err?.response?.data?.message || err?.message || '请求失败';
        if (onError) {
            onError(message);
        } else {
            Message.error(message);
        }
        return Promise.reject(err);
    }
}

export const get = async function <R>(url: string, params: GetConfig = {}, onError?: (msg: string) => void) {
    return request<R>(url, { method: 'GET', params }, onError);
};

export const post = async function <R>(url: string, body?: IndexedObject, onError?: (msg: string) => void) {
    const config: BaseInit = {
        data: typeof body === 'object' ? body : body,
        method: 'POST'
    };

    return request<R>(url, config, onError);
};

export const put = async function <R>(url: string, body?: IndexedObject, onError?: (msg: string) => void) {
    const config: BaseInit = {
        data: typeof body === 'object' ? body : body,
        method: 'PUT'
    };

    return request<R>(url, config, onError);
};

export const del = async function <R>(url: string, onError?: (msg: string) => void) {
    const config: BaseInit = {
        method: 'DELETE'
    };

    return request<R>(url, config, onError);
};

// 添加到 request 对象上以便兼容旧用法
request.get = get;
request.post = post;
request.put = put;
request.delete = del;